import { FC, useMemo } from 'react';
import { BreadcrumbList } from './BreadcrumbList';
import { Addon, ShopContent } from '../../../types/cms_shared_types';
import { Button } from '../../Common/Button';
import { ArrowLeft } from 'phosphor-react';
import { Conditional } from '../../Common/Conditional';
import { ShoppingCart } from '../ShoppingCart/ShoppingCart';
import { AddonListActions, Product, ProductListActions, Steps } from '../../../types/webshop';

const SHOPPING_CART_STEPS = [
  Steps.PRODUCT_CHOICE,
  Steps.SUMMARY,
  Steps.ADDONS,
  Steps.INFORMATION,
  Steps.INVOICE,
  Steps.CONTACT,
];

interface BreadcrumbsProps {
  currentStep: number;
  selectedCategoryKey?: string;
  selectedSubcategoryKey?: string;
  price: string;
  vatPrice: string;
  selectedProducts: Product[];
  selectedProductsActions: ProductListActions;
  selectedAddons: Addon[];
  selectedAddonsActions: AddonListActions;
  onBackClick: () => void;
  cmsShopContent: ShopContent;
  displayAddonsStep: () => boolean;
}

export const Breadcrumbs: FC<BreadcrumbsProps> = ({
  currentStep,
  selectedCategoryKey,
  selectedSubcategoryKey,
  price,
  vatPrice,
  selectedProducts,
  selectedProductsActions,
  selectedAddons,
  selectedAddonsActions,
  onBackClick,
  cmsShopContent,
  displayAddonsStep,
}) => {
  const showBreadcrumbsContent = (): boolean => currentStep <= Steps.CONTACT;

  const getBottomBorder = () => (showBreadcrumbsContent() ? 'border-b border-input-border' : '');

  const showShoppingCart = useMemo(() => {
    if (!SHOPPING_CART_STEPS.includes(currentStep)) {
      return false;
    }

    return selectedProducts.length > 0 || selectedAddons.length > 0;
  }, [currentStep, selectedAddons.length, selectedProducts.length]);

  const disableShoppingCart = () => {
    return currentStep >= Steps.LANDING_PAGE;
  };

  return (
    <div className={`w-screen h-16 flex flex-row items-center ${getBottomBorder()}`}>
      <div className="flex-none w-1/6">
        <Conditional
          condition={currentStep != Steps.PRODUCT_FAMILY && currentStep < cmsShopContent.breadcrumb.steps.length}
          trueRender={
            <div className="flex place-content-center lg:place-content-end">
              <div className="hidden lg:block">
                <Button
                  text={cmsShopContent.breadcrumb.backButton}
                  leftIcon={<ArrowLeft size={24} className="text-icon" />}
                  onClick={onBackClick}
                />
              </div>

              <button className="block ml-6 lg:hidden" onClick={onBackClick}>
                <ArrowLeft size={24} className="text-icon" />
              </button>
            </div>
          }
        />
      </div>

      <div className="text-center grow">
        <Conditional
          condition={showBreadcrumbsContent()}
          trueRender={
            <BreadcrumbList
              steps={cmsShopContent.breadcrumb.steps}
              currentStep={currentStep}
              displayAddonStep={displayAddonsStep()}
            />
          }
        />
      </div>

      <div className="flex-none w-1/6 min-w-fit">
        <Conditional
          condition={showShoppingCart}
          trueRender={
            <ShoppingCart
              selectedCategoryKey={selectedCategoryKey}
              selectedSubcategoryKey={selectedSubcategoryKey}
              price={price}
              vatPrice={vatPrice}
              selectedProducts={selectedProducts}
              selectedProductsActions={selectedProductsActions}
              selectedAddons={selectedAddons}
              selectedAddonsActions={selectedAddonsActions}
              cmsShopContent={cmsShopContent}
              disable={disableShoppingCart()}
            />
          }
        />
      </div>
    </div>
  );
};
